import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ClosedCaption } from '../datasource/generated';
import { pushPosterImageCachedImages, setVideoSourceType } from '../stories/Store/playerSlice';
import { cacheImagesAsync, LIVE_STATIC_IMAGE_SRC, MP3_STATIC_IMAGE_SRC, OD_STATIC_IMAGE_SRC } from '../utility/imageUtils';
import logger from '../utility/logger';
import { preconnectToMediaOrigin } from '../utility/playbackOptimization';
import { useAppDispatch, useAppSelector, useAppStore } from '.';
import { usePlaybackOptimization } from './usePlaybackOptimization';

interface CaptionTrack {
    file: string,
    label: string,
    kind: string,
}

export interface PlaylistItem {
    file: string,
    image?: string,
    type?: string,
    description?: string | null | undefined,
    starttime?: number,
    tracks?: Array<CaptionTrack>,
}

export const getLiveCaptionTracks = (liveCaptionsAllowed: boolean) => liveCaptionsAllowed
    ? [{
        file: 'data:text/vtt;charset=utf-8,WEBVTT%0A%0A',
        label: 'Live Captions',
        kind: 'captions',
    }]
    : undefined;

export const usePlaylist = (liveCaptionsAllowed = false) => {
    const store = useAppStore();

    const launchDetails = useAppSelector((state) => state.player.launchDetails);
    const currentSegmentItemSk = useAppSelector((state) => state.player.currentSegmentItemSk);
    const cpeMarkers = useAppSelector ((state) => state.player.launchDetails.video?.cpeMarkers);
    const requestStartTime = useAppSelector((state) => state.player.requestStartTime);
    const didImagesFinishCaching = useAppSelector((state) => state.player.didPosterImageFinishCaching);
    const cachedImages = useAppSelector((state) => state.player.cachedImages);
    const hasLoadedLaunchDetails = useAppSelector((state) => state.player.hasLoadedLaunchDetails);
    const dispatch = useAppDispatch();
    const isMp3 = launchDetails.isPortable && launchDetails.isAudioOnly;
    const videoSourceType = useAppSelector((state) => state.player.videoSourceType);
    const streamCleared = useAppSelector((state) => state.player.streamCleared);

    const [hasCachedImages, setHasCachedImages] = useState(didImagesFinishCaching);
    const {
        isEnabledForOnDemand,
        isOptimized: isPlaybackOptimized,
        options: playbackOptions,
    } = usePlaybackOptimization();

    const segment = launchDetails.segments?.find(seg => seg.itemSk === currentSegmentItemSk);
    const video = launchDetails.video ? launchDetails.video : segment?.video;
    const [searchParams] = useSearchParams();
    const positionParameter = searchParams.get('pos');

    const videoSource = useMemo(() => {
        const forceBadHls = searchParams.get('forceBadHls') === 'true';
        
        const sourceMap: Record<NonNullable<typeof videoSourceType>, string | null> = {
            hls: forceBadHls 
                ? 'https://invalid-domain-that-does-not-exist.com/bad.m3u8'
                : video?.streaming?.hls?.url ?? null,
            standard: video?.url ?? null
        };
        return videoSourceType ? sourceMap[videoSourceType] : null;
    }, [videoSourceType, video?.url, video?.streaming?.hls?.url, searchParams]);

    const coverImage = useMemo(() => {
        const coverImage = isMp3
            ? MP3_STATIC_IMAGE_SRC
            : ((video?.thumbnails && video?.thumbnails[video?.thumbnails.length - 1]?.url) || OD_STATIC_IMAGE_SRC);

        return cachedImages[coverImage] ?? coverImage;
    }, [cachedImages, isMp3, video?.thumbnails]);

    useEffect(() => {
        if (!isEnabledForOnDemand) return;

        const origin = preconnectToMediaOrigin(video?.streaming?.hls?.url ?? video?.url);

        if (origin) logger.log('Preconnected to media origin', { origin });
    }, [isEnabledForOnDemand, video?.streaming?.hls?.url, video?.url]);

    // Check if HLS URL exists on CDN
    useEffect(() => {
        let cancelled = false;

        const checkHlsUrl = async () => {
            try {
                const skipHlsCheck = searchParams.get('skipHlsCheck') === 'true';

                if (skipHlsCheck) {
                    logger.log('DEBUG: Skipping HLS check');
                    if (!cancelled) dispatch(setVideoSourceType('hls'));
                    return;
                }

                if (!video?.streaming?.hls?.url) {
                    if (!cancelled) dispatch(setVideoSourceType('standard'));
                    return;
                }

                // This probe must stay unconditional. Launch details always carry an HLS URL when
                // one is configured for the item, whether or not the HLS asset actually exists on
                // the CDN — so the URL's presence proves nothing. Relying on the player's onError
                // fallback instead (as an earlier revision of this hook did behind a flag) meant a
                // genuinely missing asset went through hls.js's full fragment retry budget before
                // JW ever fired an error, which is a visible stall, retried network requests, an
                // ERROR-tagged telemetry event even on a successful fallback, and a real risk of
                // losing the viewer's playback position — worse on every axis than the network
                // round trip this probe costs up front.
                const hlsUrl = video.streaming.hls.url;
                
                try {
                    const res = await fetch(hlsUrl, {
                        method: 'GET',
                        headers: { Range: 'bytes=0-0' },
                    });

                    if (cancelled) return;
                    
                    if (res.ok) {
                        logger.log('HLS URL exists, using HLS stream', { hlsUrl });
                        dispatch(setVideoSourceType('hls'));
                    } else {
                        logger.log('HLS URL not found, falling back to standard URL', { hlsUrl, status: res.status });
                        dispatch(setVideoSourceType('standard'));
                    }
                } catch (error) {
                    if (cancelled) return;
                    logger.log('Error checking HLS URL, falling back to standard URL', { hlsUrl, error });
                    dispatch(setVideoSourceType('standard'));
                }
            } catch (error) {
                // Catch any unexpected errors to prevent uncaught promise rejections
                if (!cancelled) {
                    logger.log('Unexpected error in checkHlsUrl', { error });
                    dispatch(setVideoSourceType('standard'));
                }
            }
        };

        const currentVideoUrl = (video?.url ?? video?.streaming?.hls?.url) ?? null;
        
        if (video && hasLoadedLaunchDetails && videoSourceType === null) {
            lastCheckedVideoRef.current = currentVideoUrl;
            checkHlsUrl();
        }
        // Re-check when segment changes (video URL changes)
        else if (video && hasLoadedLaunchDetails && videoSourceType !== null && 
            currentVideoUrl && currentVideoUrl !== lastCheckedVideoRef.current) {
            lastCheckedVideoRef.current = currentVideoUrl;
            checkHlsUrl();
        }
        
        return () => {
            cancelled = true;
        };
    }, [
        searchParams,
        video,
        hasLoadedLaunchDetails,
        videoSourceType,
        dispatch,
    ]);

    const playlist = useMemo<PlaylistItem[]>(() => {
        if (streamCleared || !hasLoadedLaunchDetails || videoSource === null) {
            return [];
        }

        if (launchDetails?.externalUrl) {
            return [{
                file: '',
                image: '',
                starttime: 0,
            }];
        }
    
        if (launchDetails?.isWebcast) {
            return [{
                file: videoSource || '',
                image: LIVE_STATIC_IMAGE_SRC,
                type: 'ivs',
                starttime: 0,
                tracks: getLiveCaptionTracks(liveCaptionsAllowed),
            }];
        }
    
        const captions = video?.closedCaptions?.map((caption: ClosedCaption) => {
            return {
                file: caption.url,
                label: caption.label,
                kind: 'captions'
            } as CaptionTrack;
        });

        const currentPlaybackProgress = store.getState().player.playbackProgress.find(x => x.itemSk === currentSegmentItemSk);
        const currentVideoPosition = currentPlaybackProgress?.position ?? 0;
        const currentVideoDuration = currentPlaybackProgress?.totalTime ?? 0;

        // Offset the position if the last watched position is the same time as cpeMarker to avoid glitch in cle timer state
        const playlistPosition = cpeMarkers?.some(x => Math.round(x.position ?? 0) === currentVideoPosition)
            ? Math.max(currentVideoPosition - 1, 0)
            : currentVideoPosition;

        return [{
            file: videoSource || '',
            image: coverImage,
            starttime: requestStartTime ?? (
                positionParameter
                    ? Number.parseInt(positionParameter)
                    : playlistPosition < currentVideoDuration
                        ? playlistPosition
                        : 0
            ),
            tracks: captions,
        }];
    }, [coverImage, cpeMarkers, currentSegmentItemSk, hasLoadedLaunchDetails, launchDetails?.externalUrl, launchDetails?.isWebcast, liveCaptionsAllowed, positionParameter, requestStartTime, store, streamCleared, video?.closedCaptions, videoSource]);

    useEffect(() => {
        if (didImagesFinishCaching) {
            setHasCachedImages(true);
            return;
        }

        const coverImage = playlist[0]?.image;

        if (!coverImage) return;

        cacheImagesAsync([coverImage], launchDetails?.isWebcast ? LIVE_STATIC_IMAGE_SRC : OD_STATIC_IMAGE_SRC)
            .then(cachedImages => {
                dispatch(pushPosterImageCachedImages(cachedImages));
                setHasCachedImages(true);
            });
    },[didImagesFinishCaching, dispatch, launchDetails?.isWebcast, playlist]);

    const hasFallenBackRef = useRef(false);
    const previousVideoUrlRef = useRef(video?.streaming?.hls?.url);
    const lastCheckedVideoRef = useRef<string | null>(null);

    // Reset fallback flag when video changes
    useEffect(() => {
        if (video?.streaming?.hls?.url !== previousVideoUrlRef.current) {
            hasFallenBackRef.current = false;
            previousVideoUrlRef.current = video?.streaming?.hls?.url;
        }
    }, [video?.streaming?.hls?.url]);

    const fallbackToStandardVideo = useCallback(() => {
        if (
            !hasFallenBackRef.current &&
            videoSourceType === 'hls' &&
            video?.url
        ) {
            hasFallenBackRef.current = true;

            logger.log('HLS playback failed, falling back to standard URL', { 
                hlsUrl: video?.streaming?.hls?.url,
                standardUrl: video.url 
            });

            dispatch(setVideoSourceType('standard'));
            return true;
        }
        return false;
    }, [videoSourceType, video?.url, video?.streaming?.hls?.url, dispatch]);

    // Withholding the playlist until the poster image has fully downloaded delays the player mount
    // by the length of an image request. The poster is decoration; the stream is the product.
    const canSkipPosterWait = isPlaybackOptimized && playbackOptions.mountBeforePosterCached;

    return {
        playlist: (hasCachedImages || canSkipPosterWait) ? playlist : [],
        video,
        segment,
        fallbackToStandardVideo
    };
};
